<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'next/icon/Icon.vue';

export default {
  components: {
    NextButton,
    Checkbox,
    Icon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
        show_in_navigation: false,
        icon: 'i-lucide-link',
      },
      iconOptions: [
        { label: 'Link', value: 'i-lucide-link' },
        { label: 'Layout Grid', value: 'i-lucide-layout-grid' },
        { label: 'Chart', value: 'i-lucide-chart-spline' },
        { label: 'Inbox', value: 'i-lucide-inbox' },
        { label: 'Message Circle', value: 'i-lucide-message-circle' },
        { label: 'Bot', value: 'i-lucide-bot' },
        { label: 'Megaphone', value: 'i-lucide-megaphone' },
        { label: 'Workflow', value: 'i-lucide-workflow' },
        { label: 'Panels Left Bottom', value: 'i-lucide-panels-left-bottom' },
        { label: 'Bar Chart 3', value: 'i-lucide-bar-chart-3' },
        { label: 'Line Chart', value: 'i-lucide-line-chart' },
        { label: 'Users', value: 'i-lucide-users' },
        { label: 'User Cog', value: 'i-lucide-user-cog' },
        { label: 'Briefcase', value: 'i-lucide-briefcase' },
        { label: 'Building 2', value: 'i-lucide-building-2' },
        { label: 'Library Big', value: 'i-lucide-library-big' },
        { label: 'Blocks', value: 'i-lucide-blocks' },
        { label: 'Tag', value: 'i-lucide-tag' },
        { label: 'Tags', value: 'i-lucide-tags' },
        { label: 'Badge Check', value: 'i-lucide-badge-check' },
        { label: 'Shield', value: 'i-lucide-shield' },
        { label: 'Shield Check', value: 'i-lucide-shield-check' },
        { label: 'Bolt', value: 'i-lucide-bolt' },
        { label: 'Rocket', value: 'i-lucide-rocket' },
        { label: 'Flame', value: 'i-lucide-flame' },
        { label: 'Sparkles', value: 'i-lucide-sparkles' },
        { label: 'Wand Sparkles', value: 'i-lucide-wand-sparkles' },
        { label: 'Star', value: 'i-lucide-star' },
        { label: 'Heart', value: 'i-lucide-heart' },
        { label: 'Clock 3', value: 'i-lucide-clock-3' },
        { label: 'Calendar Days', value: 'i-lucide-calendar-days' },
        { label: 'File Text', value: 'i-lucide-file-text' },
        { label: 'Folder', value: 'i-lucide-folder' },
        { label: 'Search', value: 'i-lucide-search' },
        { label: 'Settings', value: 'i-lucide-settings' },
        { label: 'Cog', value: 'i-lucide-cog' },
        { label: 'Sliders Horizontal', value: 'i-lucide-sliders-horizontal' },
        { label: 'Message Square Quote', value: 'i-lucide-message-square-quote' },
        { label: 'Messages Square', value: 'i-lucide-messages-square' },
        { label: 'Phone', value: 'i-lucide-phone' },
        { label: 'Mail', value: 'i-lucide-mail' },
        { label: 'Globe', value: 'i-lucide-globe' },
        { label: 'Laptop', value: 'i-lucide-laptop' },
        { label: 'Smartphone', value: 'i-lucide-smartphone' },
        { label: 'Puzzle', value: 'i-lucide-puzzle' },
        { label: 'Package', value: 'i-lucide-package' },
        { label: 'Credit Card', value: 'i-lucide-credit-card' },
        { label: 'Banknote', value: 'i-lucide-banknote' },
        { label: 'Shopping Cart', value: 'i-lucide-shopping-cart' },
        { label: 'Truck', value: 'i-lucide-truck' },
      ],
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
    selectedIconOption() {
      return (
        this.iconOptions.find(option => option.value === this.app.icon) ||
        this.iconOptions[0]
      );
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.show_in_navigation = !!this.selectedAppData.show_in_navigation;
      this.app.icon = this.selectedAppData.icon || 'i-lucide-link';
    }
  },
  methods: {
    selectIcon(iconValue) {
      this.app.icon = iconValue;
      this.$refs.iconDropdown?.removeAttribute('open');
    },
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
        show_in_navigation: false,
        icon: 'i-lucide-link',
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          show_in_navigation: !!this.app.show_in_navigation,
          icon: this.app.icon || 'i-lucide-link',
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-[42rem] overflow-visible">
      <woot-modal-header :header-title="header" />
      <form class="w-full flex flex-col flex-1 min-h-0" @submit.prevent="submit">
        <div class="flex-1 overflow-visible">
          <woot-input
            v-model="app.title"
            :class="{ error: v$.app.title.$error }"
            class="w-full"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
            "
            :error="
              v$.app.title.$error
                ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
                : null
            "
            data-testid="app-title"
            @input="v$.app.title.$touch"
            @blur="v$.app.title.$touch"
          />
          <woot-input
            v-model="app.content.url"
            :class="{ error: v$.app.content.url.$error }"
            class="w-full"
            :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
            :placeholder="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
            "
            :error="
              v$.app.content.url.$error
                ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
                : null
            "
            data-testid="app-url"
            @input="v$.app.content.url.$touch"
            @blur="v$.app.content.url.$touch"
          />
          <div class="flex items-center mt-4 mb-2">
            <Checkbox v-model="app.show_in_navigation" />
            <label class="ml-2 cursor-pointer select-none text-sm text-slate-700 dark:text-slate-200">
              {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SHOW_IN_NAVIGATION_LABEL', 'Adicionar ao menu lateral') }}
            </label>
          </div>
          <div
            v-if="app.show_in_navigation"
            class="mb-2"
          >
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2"
            >
              {{
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SIDEBAR_ICON_LABEL',
                  'Icone do menu lateral'
                )
              }}
            </label>
            <div class="flex items-center gap-2">
              <details
                ref="iconDropdown"
                class="relative w-full"
              >
                <summary
                  class="list-none w-full rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12 cursor-pointer flex items-center justify-between"
                  data-testid="app-sidebar-icon"
                >
                  <span class="flex items-center gap-2">
                    <Icon :icon="selectedIconOption.value" class="size-4" />
                    <span>{{ selectedIconOption.label }}</span>
                  </span>
                  <span class="i-lucide-chevron-down size-4 text-n-slate-10" />
                </summary>
                <div class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-n-weak bg-n-background shadow-lg p-1">
                  <button
                    v-for="iconOption in iconOptions"
                    :key="iconOption.value"
                    type="button"
                    class="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm text-left text-n-slate-12 hover:bg-n-alpha-2"
                    @click="selectIcon(iconOption.value)"
                  >
                    <span class="flex items-center gap-2">
                      <Icon :icon="iconOption.value" class="size-4" />
                      <span>{{ iconOption.label }}</span>
                    </span>
                    <span
                      v-if="app.icon === iconOption.value"
                      class="rounded-md px-1.5 py-0.5 text-[10px] leading-4 font-medium uppercase tracking-wide bg-n-blue-text text-n-alpha-white"
                    >
                      Selecionado
                    </span>
                  </button>
                </div>
              </details>
            </div>
          </div>
        </div>
        <div class="mt-auto flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
